import {
  useFormFactory,
  getFormatDate,
  getQuestionFromElements,
  type Group,
  type Question as QuestionType,
} from '@meilleurtaux/form-factory'
import { Col, Row, Field, Message, useTracking } from '@meilleurtaux/form-factory-ds'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { Controller } from 'react-hook-form'
import { type AnyObject, type ISchema, type ObjectSchema } from 'yup'
import type { Icon } from '@meilleurtaux/form-factory-ds'
import type React from 'react'
import type {
  Control,
  FieldErrors,
  FieldValues,
  Noop,
  RefCallBack,
  UseFormGetFieldState,
} from 'react-hook-form'
import { shouldSkipInlineSave, transformResponse } from 'utils/questionManager'
import { storage } from 'utils/storage'

// #region interfaces
type IconType = React.ComponentProps<typeof Icon>['icon']

interface QuestionManagerProps {
  currentStep: Group
  control: Control<FieldValues>
  errors: FieldErrors<FieldValues>
  lastQuestionExtraInfo?: string
  validationSchema: ObjectSchema<
    {
      [x: string]: unknown
    },
    AnyObject,
    {
      [x: string]: undefined
    },
    ''
  >
  questionRefs?: React.MutableRefObject<
    {
      extraInfo: string
      ref: HTMLElement
    }[]
  >
  goNextOnSelect: boolean
  getFieldState: UseFormGetFieldState<{
    [key: string]: string
  }>
  saveResponse: (responses: { key: string; response: string }[]) => void
}

// #region helpers
const overrideFocus = (
  elem: HTMLElement | null,
  ref: RefCallBack,
  question: QuestionType,
  questionsRef: React.MutableRefObject<
    {
      extraInfo: string
      ref: HTMLElement
    }[]
  >,
) => {
  ref({
    ...elem,
    focus: () => {
      elem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      elem?.focus({ preventScroll: true })
    },
  })

  // Ajouter la ref à questionRefs pour la détection de scroll
  pushQuestionRef(questionsRef, question, elem)
}

const pushQuestionRef = (
  questionsRef: React.MutableRefObject<
    {
      extraInfo: string
      ref: HTMLElement
    }[]
  >,
  question: QuestionType,
  elem: HTMLElement | null,
) => {
  if (!elem) return

  const existingRefIndex = questionsRef.current.findIndex((q) => q.extraInfo === question.extraInfo)
  if (existingRefIndex === -1) {
    questionsRef.current.push({ extraInfo: question.extraInfo, ref: elem })
  } else {
    questionsRef.current[existingRefIndex].ref = elem
  }
}

// #region renderQuestion
const renderQuestion = (
  question: QuestionType,
  message: string,
  labelQuestion: string,
  fieldInError: boolean,
  control: Control<FieldValues>,
  isDirty: boolean,
  isLastField: boolean,
  questionsRef: React.MutableRefObject<
    {
      extraInfo: string
      ref: HTMLElement
    }[]
  >,
  handleOnBlur: (onBlur: Noop, value: string, questionExtraInfo: string) => void,
  handleOnChange: (onChange: (...event: unknown[]) => void, onBlur: Noop, value: string) => void,
  goNextOnSelect: boolean,
  responses: ReturnType<typeof useFormFactory>['responses'],
  extraInfoWarning?: string,
) => {
  let fieldStatus: 'error' | 'warning' | undefined
  if (fieldInError) {
    fieldStatus = 'error'
  } else if (extraInfoWarning === question.extraInfo) {
    fieldStatus = 'warning'
  }

  const commonFieldProps = {
    extraInfo: question.extraInfo,
    label: labelQuestion,
    message,
    status: fieldStatus,
    template: question.template ?? 'default',
    lastField: isLastField,
    subtitle: question.interMessage,
    tooltipContent: question.tooltip,
    tooltipTitle: question.tooltipTitle,
    isOptional: question.optional,
    isDisplayOptional: question.displayOptional,
  }

  switch (question.validatorKind) {
    case 'FreeTextNumber':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, ref, ...fieldProps } }) => (
            <Field
              type="number"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              placeholder={
                'placeholder' in question.validatorConfig
                  ? question.validatorConfig.placeholder
                  : ''
              }
              symbole={
                'symbole' in question.validatorConfig ? question.validatorConfig.symbole : undefined
              }
              onBlur={() => handleOnBlur(onBlur, fieldProps.value, question.extraInfo)}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'Date':
      return (
        <Controller
          name={`date_${question.extraInfo}`}
          control={control}
          render={({ field: { value, onBlur, onChange, ref, ...fieldProps } }) => {
            const format = getFormatDate(
              question.validatorConfig.jour,
              question.validatorConfig.mois,
            )
            return (
              <Field
                type="date"
                day={question.validatorConfig.jour}
                month={question.validatorConfig.mois}
                year={true}
                valueSelected={value}
                values={question.availableResponses
                  .filter((resp) => resp.display)
                  .map((resp) => ({
                    text: resp.label,
                    value: resp.value,
                    icon: (resp.icon as IconType) ?? undefined,
                  }))}
                min={dayjs(question.validatorConfig.min).format(format)}
                max={dayjs(question.validatorConfig.max).format(format)}
                modeAffichage={question.validatorConfig.modeAffichage}
                selectLabelDay={question.validatorConfig.selectLabelDay}
                selectLabelMonth={question.validatorConfig.selectLabelMonth}
                selectLabelYear={question.validatorConfig.selectLabelYear}
                provideValue={(value) => handleOnChange(onChange, onBlur, value)}
                isSubmitButton={false}
                otherLabel={question.validatorConfig.otherLabel}
                onBlur={onBlur}
                ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
                {...commonFieldProps}
                {...fieldProps}
              />
            )
          }}
        />
      )
    case 'FreeTextEmail':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, ref, ...fieldProps } }) => (
            <Field
              type="email"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              placeholder={question.validatorConfig.placeholder}
              onBlur={() => handleOnBlur(onBlur, fieldProps.value, question.extraInfo)}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'FreeTextPhone':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, ref, ...fieldProps } }) => (
            <Field
              type="phone"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              placeholder={question.validatorConfig.placeholder}
              onBlur={() => handleOnBlur(onBlur, fieldProps.value, question.extraInfo)}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'ZipCodeCity':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="zipCodeCity"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              placeholder={
                question.validatorConfig.placeholder !== ''
                  ? question.validatorConfig.placeholder
                  : undefined
              }
              onChange={(value) => handleOnChange(onChange, onBlur, value)}
              onBlur={onBlur}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'ValueListSlider':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="valueListSlider"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              valueSelected={fieldProps.value}
              provideValue={(value) => handleOnChange(onChange, onBlur, value)}
              values={question.availableResponses
                .filter((resp) => resp.display)
                .map((resp) => ({
                  text: resp.label,
                  value: resp.value,
                }))}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'ValueListButton':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="valueListButton"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              valueSelected={fieldProps.value}
              provideValue={(value) => handleOnChange(onChange, onBlur, value)}
              values={question.availableResponses
                .filter(
                  (resp) =>
                    resp.display &&
                    !question.validatorConfig.other?.includes(Number.parseInt(resp.value)),
                )
                .map((resp) => ({
                  text: resp.label,
                  value: resp.value,
                  icon: (resp.icon as IconType) ?? undefined,
                }))}
              isSubmitButton={goNextOnSelect}
              otherLabel={question.validatorConfig.selectLabel}
              others={question.availableResponses
                .filter(
                  (resp) =>
                    resp.display &&
                    question.validatorConfig.other?.includes(Number.parseInt(resp.value)),
                )
                .map((resp) => ({
                  text: resp.label,
                  value: resp.value,
                  icon: (resp.icon as IconType) ?? undefined,
                }))}
              multiple={question.validatorConfig.multiple}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'ValueListCheckbox':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onChange, onBlur, ref, ...fieldProps } }) => (
            <Field
              type="valueListCheckBox"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              valueSelected={fieldProps.value}
              values={question.availableResponses
                .filter((resp) => resp.display)
                .map((resp) => ({
                  text: resp.label,
                  value: resp.value,
                }))}
              multiple={question.validatorConfig.multiple}
              onChange={(value) => handleOnChange(onChange, onBlur, value)}
              onBlur={onBlur}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'ValueListSelect':
    case 'BonusMalus':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="select"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              placeholder={question.validatorConfig.selectLabel}
              options={question.availableResponses
                .filter((resp) => resp.display)
                .map((resp) => ({
                  label: resp.label,
                  value: resp.value,
                }))}
              multiple={'multiple' in question.validatorConfig && question.validatorConfig.multiple}
              onChange={(value) => handleOnChange(onChange, onBlur, value)}
              onBlur={onBlur}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'ValueListRadio':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="valueListTab"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              valueSelected={fieldProps.value}
              provideValue={(value) => handleOnChange(onChange, onBlur, value)}
              options={question.availableResponses
                .filter(
                  (resp) =>
                    resp.display &&
                    !question.validatorConfig.other?.includes(Number.parseInt(resp.value)),
                )
                .map((resp) => ({
                  text: resp.label,
                  value: resp.value,
                  icon: (resp.icon as IconType) ?? undefined,
                }))}
              multiple={question.validatorConfig.multiple}
              isSubmitButton={false}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'Vehicle':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="vehicle"
              vehicleValidatorConfig={question.validatorConfig}
              onChange={(value) => handleOnChange(onChange, onBlur, value)}
              onBlur={onBlur}
              userSheetId={storage.getValue('FF_UserSheetId') ?? ''}
              responses={responses}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'AutoComplete':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ref, ...fieldProps } }) => (
            <Field
              type="autocomplete"
              autoCompleteType={question.validatorConfig.type}
              placeholder={question.validatorConfig.placeholder}
              onChange={(value) => handleOnChange(onChange, onBlur, value)}
              onBlur={onBlur}
              isDirty={isDirty}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
    case 'Optin':
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, onChange, ...fieldProps } }) => (
            <Field
              type="optin"
              texteOptin={question.validatorConfig.texteOptin}
              sousTexteOptin={question.validatorConfig.sousTexteOptin}
              afficherCoche={question.validatorConfig.afficherCoche}
              isChecked={fieldProps.value === '1'}
              onChange={(value) => handleOnChange(onChange, onBlur, value)}
              {...commonFieldProps}
            />
          )}
        />
      )
    case 'FreeTextFree':
    default:
      return (
        <Controller
          name={question.extraInfo}
          control={control}
          render={({ field: { onBlur, ref, ...fieldProps } }) => (
            <Field
              type="text"
              labelIcon={
                labelQuestion === undefined
                  ? undefined
                  : (question.labelIcon as IconType | undefined)
              }
              placeholder={
                'placeholder' in question.validatorConfig
                  ? question.validatorConfig.placeholder
                  : ''
              }
              onBlur={() => handleOnBlur(onBlur, fieldProps.value, question.extraInfo)}
              ref={(elem) => overrideFocus(elem, ref, question, questionsRef)}
              {...commonFieldProps}
              {...fieldProps}
            />
          )}
        />
      )
  }
}

// #region Component
export default function QuestionManager({
  currentStep,
  control,
  errors,
  validationSchema,
  lastQuestionExtraInfo,
  questionRefs: externalQuestionRefs,
  goNextOnSelect,
  getFieldState,
  saveResponse,
}: Readonly<QuestionManagerProps>) {
  const { logError, extraInfoWarning, responses } = useFormFactory(import.meta.env.VITE_API_URL)
  const internalQuestionRef = useRef<{ extraInfo: string; ref: HTMLElement }[]>([])
  const questionRefs = externalQuestionRefs || internalQuestionRef
  const { trackQuestionAnswered } = useTracking()

  // On ne prend que les questions qui sont affichées
  const questionsVisible = getQuestionFromElements(currentStep.elements).filter(
    (question) => question.display,
  )
  const hasQuestionVisible = questionsVisible.length > 0
  const questionsAndMessagesVisible = hasQuestionVisible
    ? currentStep.elements.filter(
        (el) => el.type === 'message' || (el.type === 'question' && el.display),
      )
    : []

  // #region Component.render
  return (
    <>
      {questionsAndMessagesVisible.map((element) => {
        if (element.type === 'message') {
          if (element.display) {
            return (
              <Fragment key={element.message}>
                <Row gap={16} style={{ marginLeft: 0, marginRight: 0 }}>
                  <Col gap={16} col={12}>
                    <Message
                      template={element.messageTemplate ?? 'default'}
                      message={element.message ?? ''}
                    />
                  </Col>
                </Row>
              </Fragment>
            )
          } else {
            return null
          }
        }

        const extraInfo =
          element.validatorKind === 'Date' ? `date_${element.extraInfo}` : element.extraInfo
        // Si on affiche le message d'erreur dans le state, on met le message d'erreur de la question,
        // Sinon on regarde si on a des messages d'erreurs via react hook form et le yup

        const message = element.displayErrorMessage
          ? element.errorMessage
          : (errors[extraInfo]?.message?.toString() ?? '')

        // On regarde si le champs est en erreur
        const fieldInError = element.displayErrorMessage || errors[extraInfo]?.message !== undefined

        const handleBlur = async (onBlur: Noop, value: string, questionExtraInfo: string) => {
          const validationSchemaQuestion = validationSchema.fields[extraInfo] as ISchema<
            unknown,
            AnyObject
          >

          let isValid = true
          try {
            await validationSchemaQuestion.validate(value)
          } catch {
            isValid = false
            logError({
              userSheetId: storage.getValue('FF_UserSheetId') ?? '',
              extraInfo: questionExtraInfo,
              userAnswer: value,
              tunnelId: Number.parseInt(storage.getValue('FF_TunnelId') ?? '0'),
              variationId: Number.parseInt(storage.getValue('FF_VariationId') ?? '0'),
            })
          }

          // On déclenche le blur de react hook form afin de gérer l'état et d'afficher les erreurs si nécessaire
          onBlur()

          // Une réponse invalide (ex: date hors bornes) ne doit pas être propagée au moteur de
          // formulaire : celui-ci recalcule en direct les questions dépendantes (permis, bonus/malus...)
          // à partir de la valeur brute et n'est pas garanti robuste à des valeurs hors bornes.
          // Seul le message d'erreur (déclenché par onBlur ci-dessus) doit être affiché.
          if (!isValid) return

          // On transforme certaines réponses (ex: date)
          const responses = transformResponse(
            [{ key: questionExtraInfo, response: value }],
            currentStep,
          )

          saveResponse(responses)

          for (const { key } of responses) {
            const question = getQuestionFromElements(currentStep.elements).find(
              (q) => q.extraInfo === key,
            )

            if (!question?.label) continue
            trackQuestionAnswered({
              questionExtraInfo: key,
              questionTitle: question.label,
              questionOrder: question.order,
            })
          }
        }

        const handleChange = (
          onChange: (...event: unknown[]) => void,
          onBlur: Noop,
          value: string,
        ) => {
          onChange(value)
          if (shouldSkipInlineSave(element.validatorKind, goNextOnSelect)) return
          handleBlur(onBlur, control._formValues[extraInfo], element.extraInfo)
        }

        const isDirty = getFieldState(extraInfo)?.isDirty

        return (
          <Fragment key={element.extraInfo}>
            {renderQuestion(
              element,
              message,
              element.label || '',
              fieldInError,
              control,
              isDirty,
              element.extraInfo === lastQuestionExtraInfo,
              questionRefs,
              handleBlur,
              handleChange,
              goNextOnSelect,
              responses,
              extraInfoWarning,
            )}
          </Fragment>
        )
      })}
    </>
  )
}
